import { useEffect, useRef, useState } from "react";
import { Paperclip, Send } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Avatar } from "@/components/common";
import { cn } from "@/lib/utils";

const Bubble = ({ m }) => {
  const right = m.from !== "client";
  return (
    <div data-testid={`chat-message-${m.id}`} className={cn("flex items-end gap-2", right && "flex-row-reverse")}>
      <Avatar name={m.name} size="sm" />
      <div className={cn("max-w-[78%] sm:max-w-[65%]", right && "text-right")}>
        <div className={cn("mb-1 flex items-center gap-2 text-[11px] text-slate-500", right && "justify-end")}>
          <span className="font-semibold text-slate-700">{m.name}</span>
          {m.from !== "client" && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-800">{m.from}</span>}
          <span>{m.time}</span>
        </div>
        <div className={cn("inline-block rounded-2xl px-4 py-2.5 text-left text-sm leading-relaxed",
          right ? "rounded-br-md bg-navy text-white" : "rounded-bl-md border border-slate-200 bg-white text-slate-700")}>
          {m.text}
        </div>
      </div>
    </div>
  );
};

export const ChatThread = ({ initial }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState(initial);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  const send = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    setMessages((m) => [...m, { id: Date.now(), from: user.user_type, name: user.name, time, text: text.trim() }]);
    setText("");
  };

  return (
    <div className="flex flex-col">
      <div data-testid="chat-thread-list" className="flex flex-col gap-5 rounded-2xl bg-slate-50 p-4 sm:p-6">
        <div className="mx-auto rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-slate-500 shadow-sm">Today</div>
        {messages.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="sticky bottom-[76px] md:bottom-4 mt-3 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2 shadow-soft">
        <button type="button" data-testid="chat-attach-button" className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-slate-400 hover:bg-slate-100" aria-label="Attach"><Paperclip size={18} /></button>
        <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="h-11 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400" />
        <button data-testid="chat-send-button" type="submit" className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-gold px-4 text-sm font-semibold text-white transition-colors hover:bg-gold-hover active:scale-95"><Send size={16} /><span className="hidden sm:inline">Send</span></button>
      </form>
    </div>
  );
};
